import React, { useState, useEffect, memo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
// navigation is performed via window.location to avoid requiring react-router here
import { QlikEmbed } from '@qlik/embed-react';
import { getVisualizationProps } from '../../services/qlikService';
import { THEME } from '../../constants/qlik';
import './QlikKpi.scss';

// Memoized wrapper for QlikEmbed to avoid unnecessary re-renders
const MemoizedQlikEmbed = memo(QlikEmbed, (prevProps: any, nextProps: any) => {
  return (
    prevProps.appId === nextProps.appId &&
    prevProps.objectId === nextProps.objectId &&
    prevProps.ui === nextProps.ui &&
    prevProps.iframe === nextProps.iframe &&
    prevProps.theme === nextProps.theme &&
    JSON.stringify(prevProps.context) === JSON.stringify(nextProps.context)
  );
});

MemoizedQlikEmbed.displayName = 'MemoizedQlikEmbed';

interface QlikKpiProps {
  qlikVizName: string;
  qlikTitle?: string;
  backColor?: string;
  goToState?: string;
  enableInteractions?: boolean;
  short?: boolean;
}

const QlikKpi: React.FC<QlikKpiProps> = ({ 
    qlikVizName,
    qlikTitle,
    backColor,
    goToState,
    enableInteractions = false,
    short = false 
}) => {

  const [vizProps, setVizProps] = useState<any>(null);
  const [interactions, setInteractions] = useState<any>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
      initQlikObject();
  }, [qlikVizName]);

  const initQlikObject = async () => {
    try {
        if(!vizProps) {
            const vizPropsData = await getVisualizationProps(qlikVizName);
            setVizProps(vizPropsData);
            if(vizPropsData?.vizDef?.ui === 'analytics/chart') {
              setInteractions({
                interactions : {
                  active: typeof enableInteractions === 'boolean' ? (enableInteractions ? true : false) : false,
                  select: false,
                  passive: true,
                },
              });
            } else {
              setInteractions({});
            }
        }
        setReady(true);
    }
    catch (error) {
      console.error('Error initializing Qlik KPI object:', error);
    }
 };
  
  const handleClick = () => {
    if (goToState) {
      // Fall back to setting location.href — the caller can pass a route/path
      window.location.href = goToState;
    }
  };

  return (
    <Box
      id={vizProps?.vizDef?.id}
      onClick={handleClick}
      sx={{
        cursor: goToState ? 'pointer' : 'default',
        backgroundColor: backColor || 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // sizes roughly matching the original 'short' behaviour
        height: short ? 100 : 150,
        borderRadius: 1,
        overflow: 'hidden',
      }}
      data-qlik-kpi
      className="qlikKpi"
    >
        {/* Render the Qlik object using QlikEmbed. The embed library will mount the visualization into its container. */}
        {vizProps && ready &&  (
            <MemoizedQlikEmbed
                appId={ vizProps?.appId }
                ui={ vizProps?.vizDef?.ui }
                objectId={ vizProps?.vizDef?.id }
                iframe={ vizProps?.vizDef?.iframe }
                theme={ THEME }
                context={ interactions } 
            />
        )}

      {/* Optional title overlay for accessibility/labeling */}
      {qlikTitle && (
        <Box sx={{ position: 'absolute', left: 8, top: 8 }}>
          <Typography variant="caption" sx={{ color: 'text.primary' }}>
            {qlikTitle}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QlikKpi;
